// EegWellnessDashboard.js
// Patient-facing WELLNESS view — no raw EEG or clinical scores shown.
// All outputs are motivational derivatives: % improvements, streaks, badges.
// Regulatory intent: keeps this below Class III SaMD threshold (wellness / general fitness).
// Clinical data remains in EegPatientDashboard (clinician-only access).

import React, { useEffect, useState, useCallback } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getToken, getUser } from "redux/services";
import {
  EEG_PROGRESS_API,
  EEG_SESSION_LATEST_API,
  EEG_PROGRESS_EX_API,
} from "redux/requests/apis";

/* ── fetch ──────────────────────────────────────────────────────────────── */
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

async function apiFetch(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

/* ── Panel header — matches EegCognitiveDashboard / EegClinicianDashboard ── */
const PanelHeader = ({ icon, title, color = "dark" }) => (
  <MDBox display="flex">
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="4rem"
      height="4rem"
      variant="gradient"
      bgColor={color}
      color="white"
      shadow="md"
      borderRadius="xl"
      ml={3}
      mt={-2}
    >
      <Icon fontSize="medium" color="inherit">{icon}</Icon>
    </MDBox>
    <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
      {title}
    </MDTypography>
  </MDBox>
);

/* ── convert clinical data → motivational language ──────────────────────── */
function deriveWellnessStory(progress, latest, exercises) {
  if (!progress && !latest) return null;

  const totalSessions  = progress?.totalSessions  ?? 0;
  const erdChange      = progress?.erdChange       ?? 0;
  const asymChange     = progress?.asymmetryChange ?? 0;
  const erdPresent     = progress?.erdNowPresent   ?? false;
  const sessionNum     = latest?.sessionNumber     ?? totalSessions;
  const topExercise    = latest?.rankedExercises?.[0] ?? null;

  const engagementImprovement = Math.min(99, Math.round(Math.abs(erdChange)));
  const balanceImprovement    = Math.min(99, Math.round(Math.abs(asymChange) * 200));
  const brainScore = Math.min(100, Math.round(
    50 + Math.abs(erdChange) * 1.2 + Math.abs(asymChange) * 40
  ));

  const topExercises = [];
  if (exercises?.length) {
    const allResults = exercises.flatMap((s) => s.exerciseResults ?? []);
    const map = {};
    allResults.forEach(({ label, effectiveness }) => {
      if (!map[label]) map[label] = { label, total: 0, count: 0 };
      map[label].total += Math.abs(effectiveness ?? 0);
      map[label].count += 1;
    });
    Object.values(map)
      .sort((a, b) => b.total / b.count - a.total / a.count)
      .slice(0, 3)
      .forEach((e) => topExercises.push(e.label));
  }
  if (topExercise && !topExercises.includes(topExercise)) topExercises.unshift(topExercise);

  const streak = Math.min(sessionNum, 7);

  const badges = [];
  if (totalSessions >= 1)  badges.push({ icon: "emoji_events",          label: "First Session",        color: "warning" });
  if (totalSessions >= 3)  badges.push({ icon: "local_fire_department",  label: "3 Sessions Strong",    color: "error"   });
  if (totalSessions >= 5)  badges.push({ icon: "bolt",                   label: "Half Way There",       color: "secondary" });
  if (totalSessions >= 7)  badges.push({ icon: "star",                   label: "Week Warrior",         color: "info"    });
  if (totalSessions >= 10) badges.push({ icon: "workspace_premium",      label: "10 Session Champion",  color: "success" });
  if (erdPresent)          badges.push({ icon: "psychology",             label: "Brain Activated",      color: "dark"    });

  let headline = "Keep building that momentum!";
  if (engagementImprovement >= 15)      headline = `Your brain activity improved ${engagementImprovement}% — incredible progress!`;
  else if (engagementImprovement >= 8)  headline = `Your brain activity improved ${engagementImprovement}% this week — keep going!`;
  else if (engagementImprovement >= 3)  headline = `Your brain is responding more — ${engagementImprovement}% stronger since you started!`;
  else if (totalSessions === 1)         headline = "Great start! Your brain is already responding to the exercises.";

  const messages = [];
  if (balanceImprovement > 0) messages.push(`Your brain coordination improved ${balanceImprovement}% since session 1.`);
  if (erdPresent) messages.push("Your brain is actively engaging during the exercises — that's exactly what we want to see.");
  else            messages.push("Your brain is still warming up — every session builds the pathway a little more.");
  if (topExercises[0]) messages.push(`"${topExercises[0]}" is giving your brain the best workout right now.`);

  const nextTip = topExercises[0]
    ? `Try to do at least 3 rounds of "${topExercises[0]}" next session — your brain responds really well to it.`
    : "Focus on imagining the movement clearly before you do it — that primes your brain even before you move.";

  return {
    headline, messages, nextTip, brainScore,
    engagementImprovement, balanceImprovement,
    totalSessions, streak, badges,
    topExercises: topExercises.slice(0, 3),
    sessionsRemaining: Math.max(0, 14 - totalSessions),
  };
}

/* ── Progress ring (SVG) ────────────────────────────────────────────────── */
const RING_COLORS = {
  purple:  "#AB47BC",
  info:    "#26C6DA",
  success: "#66BB6A",
  warning: "#FFA726",
  dark:    "#344767",
};

function ProgressRing({ value, size = 120, label, sublabel, color = "purple" }) {
  const hex  = RING_COLORS[color] || RING_COLORS.purple;
  const r    = (size - 16) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <MDBox display="flex" flexDirection="column" alignItems="center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f2f5" strokeWidth={10} />
        <circle
          cx={size/2} cy={size/2} r={r}
          fill="none" stroke={hex} strokeWidth={10}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
        />
        <text x={size/2} y={size/2 - 4} textAnchor="middle" fontSize="18" fontWeight="700" fill="#344767">{value}</text>
        <text x={size/2} y={size/2 + 12} textAnchor="middle" fontSize="9" fill="#7b809a">{sublabel}</text>
      </svg>
      <MDTypography variant="caption" color="text" fontWeight="medium" mt={0.5} textAlign="center">
        {label}
      </MDTypography>
    </MDBox>
  );
}

/* ── Streak row ─────────────────────────────────────────────────────────── */
function StreakRow({ streak, total }) {
  return (
    <MDBox display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
      {Array.from({ length: Math.min(total, 14) }).map((_, i) => (
        <Icon key={i} sx={{ fontSize: 22, color: i < streak ? "#FFA726" : "#e0e0e0" }}>
          local_fire_department
        </Icon>
      ))}
    </MDBox>
  );
}

/* ── Badge chip — uses MD gradient boxes to match theme ─────────────────── */
function BadgeChip({ icon, label, color }) {
  return (
    <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: 72, textAlign: "center" }}>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="2.8rem"
        height="2.8rem"
        variant="gradient"
        bgColor={color}
        color="white"
        shadow="sm"
        borderRadius="xl"
      >
        <Icon fontSize="small" color="inherit">{icon}</Icon>
      </MDBox>
      <MDTypography variant="caption" color="text" sx={{ fontSize: 9, mt: 0.5, lineHeight: 1.3 }}>
        {label}
      </MDTypography>
    </MDBox>
  );
}

/* ── Stat tile — matches EegCognitiveDashboard session stats ─────────────── */
function StatTile({ label, value, color }) {
  return (
    <MDBox
      sx={{ background: ({ palette: { grey } }) => grey[100], borderRadius: 2, p: 1.5, textAlign: "center" }}
    >
      <MDTypography variant="h5" fontWeight="bold" sx={{ color }}>
        {value}
      </MDTypography>
      <MDTypography variant="caption" color="text" sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </MDTypography>
    </MDBox>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function EegWellnessDashboard() {
  const userId = getUser();

  const [story,   setStory]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const [prog, lat, ex] = await Promise.allSettled([
        apiFetch(EEG_PROGRESS_API(userId)),
        apiFetch(EEG_SESSION_LATEST_API(userId)),
        apiFetch(EEG_PROGRESS_EX_API(userId)),
      ]);
      setStory(deriveWellnessStory(
        prog.status === "fulfilled" ? prog.value : null,
        lat.status  === "fulfilled" ? lat.value  : null,
        ex.status   === "fulfilled" ? ex.value   : [],
      ));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  /* ── Demo data when no API response yet ──────────────────────────────── */
  const demo = story ?? {
    headline:              "Your brain activity improved 12% this week — keep going!",
    messages: [
      "Your brain coordination improved 24% since session 1.",
      "Your brain is actively engaging during the exercises — that's exactly what we want to see.",
      '"Finger tapping" is giving your brain the best workout right now.',
    ],
    nextTip:               'Try to do at least 3 rounds of "Finger tapping" — your brain responds really well to it.',
    brainScore:            72,
    engagementImprovement: 12,
    balanceImprovement:    24,
    totalSessions:         5,
    streak:                5,
    sessionsRemaining:     9,
    topExercises:          ["Finger tapping", "Arm reach", "Wrist rotation"],
    badges: [
      { icon: "emoji_events",          label: "First Session",      color: "warning"   },
      { icon: "local_fire_department", label: "3 Sessions Strong",  color: "error"     },
      { icon: "bolt",                  label: "Half Way There",     color: "secondary" },
      { icon: "psychology",            label: "Brain Activated",    color: "dark"      },
    ],
  };

  const programmePct = Math.round((demo.totalSessions / 14) * 100);

  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── Hero headline card — uses MD gradient variant ─────────────── */}
        <Card sx={{ mb: 3, overflow: "visible" }}>
          <MDBox
            variant="gradient"
            bgColor="dark"
            borderRadius="lg"
            coloredShadow="dark"
            mx={2}
            mt={-3}
            p={2}
            mb={1}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              variant="gradient"
              bgColor="secondary"
              color="white"
              shadow="md"
              borderRadius="xl"
            >
              <Icon fontSize="medium" color="inherit">psychology</Icon>
            </MDBox>
            <MDBox flex={1}>
              <MDTypography variant="h5" color="white" fontWeight="bold">
                {demo.headline}
              </MDTypography>
              <MDTypography variant="body2" color="white" opacity={0.75} mt={0.5}>
                Session {demo.totalSessions} complete · {demo.sessionsRemaining} sessions to your goal
              </MDTypography>
            </MDBox>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              variant="gradient"
              bgColor="success"
              borderRadius="lg"
              px={2}
              py={1}
            >
              <MDTypography variant="h6" color="white" fontWeight="bold">
                +{demo.engagementImprovement}%
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox p={2} />
        </Card>

        {/* ── Row 1: Progress rings + Streak ───────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ pt: 3, height: "100%" }}>
              <PanelHeader icon="show_chart" title="Your Progress This Week" color="info" />
              <MDBox px={3} pb={3}>
                <MDBox display="flex" justifyContent="space-around" flexWrap="wrap" gap={2} mt={1}>
                  <ProgressRing
                    value={demo.brainScore}
                    label="Brain Activity Score"
                    sublabel="/ 100"
                    color="purple"
                    size={120}
                  />
                  <ProgressRing
                    value={demo.balanceImprovement}
                    label="Brain Coordination"
                    sublabel="% improved"
                    color="info"
                    size={100}
                  />
                  <ProgressRing
                    value={programmePct}
                    label="Programme Progress"
                    sublabel="% done"
                    color="success"
                    size={100}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ pt: 3, height: "100%" }}>
              <PanelHeader icon="local_fire_department" title="Session Streak" color="warning" />
              <MDBox px={3} pb={3}>
                <MDTypography variant="body2" color="text" mb={2}>
                  {demo.streak} session{demo.streak !== 1 ? "s" : ""} in a row — you&apos;re on fire!
                </MDTypography>
                <StreakRow streak={demo.streak} total={14} />
                <MDBox mt={2}>
                  <LinearProgress
                    variant="determinate"
                    value={programmePct}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: ({ palette: { grey } }) => grey[200],
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        background: "linear-gradient(90deg, #344767, #7b809a)",
                      },
                    }}
                  />
                  <MDBox display="flex" justifyContent="space-between" mt={0.5}>
                    <MDTypography variant="caption" color="text">Session {demo.totalSessions}</MDTypography>
                    <MDTypography variant="caption" color="text">{demo.sessionsRemaining} to go</MDTypography>
                    <MDTypography variant="caption" color="text">14 sessions</MDTypography>
                  </MDBox>
                </MDBox>
                <Divider />
                <Grid container spacing={1.5} mt={0.5}>
                  {[
                    { label: "Brain Score",   value: `${demo.brainScore}`,            color: "#AB47BC" },
                    { label: "Coordination",  value: `+${demo.balanceImprovement}%`,  color: "#26C6DA" },
                    { label: "Sessions Done", value: `${demo.totalSessions}`,          color: "#66BB6A" },
                    { label: "Sessions Left", value: `${demo.sessionsRemaining}`,      color: "#FFA726" },
                  ].map((s) => (
                    <Grid item xs={6} key={s.label}>
                      <StatTile label={s.label} value={s.value} color={s.color} />
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Row 2: Insights + Achievements ───────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ pt: 3, height: "100%" }}>
              <PanelHeader icon="tips_and_updates" title="What Your Brain Is Telling Us" color="success" />
              <MDBox px={3} pb={3}>
                {demo.messages.map((msg, i) => (
                  <MDBox
                    key={i}
                    display="flex"
                    alignItems="flex-start"
                    gap={1.5}
                    mb={1.5}
                    sx={{
                      background: ({ palette: { grey } }) => grey[100],
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    <Icon sx={{ color: "#66BB6A", mt: 0.2, fontSize: 18 }}>check_circle</Icon>
                    <MDTypography variant="body2" color="text">{msg}</MDTypography>
                  </MDBox>
                ))}
                <MDBox
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  p={2}
                  mt={1}
                >
                  <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Icon sx={{ color: "#fff", fontSize: 18 }}>emoji_objects</Icon>
                    <MDTypography variant="button" fontWeight="bold" color="white">
                      Tip for next session
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="white" opacity={0.9}>
                    {demo.nextTip}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ pt: 3, height: "100%" }}>
              <PanelHeader icon="emoji_events" title="Your Achievements" color="warning" />
              <MDBox px={3} pb={3}>
                <MDBox display="flex" flexWrap="wrap" gap={2} justifyContent="center" mb={2.5} mt={1}>
                  {demo.badges.map((b) => (
                    <BadgeChip key={b.label} icon={b.icon} label={b.label} color={b.color} />
                  ))}
                  {demo.totalSessions < 7 && (
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      sx={{ width: 72, opacity: 0.3 }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="2.8rem"
                        height="2.8rem"
                        sx={{ borderRadius: "12px", border: "2px dashed #bdbdbd", background: ({ palette: { grey } }) => grey[100] }}
                      >
                        <Icon sx={{ color: "#bdbdbd", fontSize: 20 }}>lock</Icon>
                      </MDBox>
                      <MDTypography variant="caption" color="text" sx={{ fontSize: 9, mt: 0.5 }}>
                        Week Warrior
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
                <Divider />
                <MDTypography variant="button" fontWeight="bold" color="text" display="block" mt={1.5} mb={1}>
                  Your Best Exercises
                </MDTypography>
                {demo.topExercises.map((ex, i) => (
                  <MDBox key={ex} display="flex" alignItems="center" gap={1.5} mb={1}>
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="1.5rem"
                      height="1.5rem"
                      variant="gradient"
                      bgColor={["warning", "info", "success"][i]}
                      color="white"
                      borderRadius="md"
                    >
                      <MDTypography variant="caption" color="white" fontWeight="bold" sx={{ fontSize: 10 }}>
                        {i + 1}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="button" color="text">{ex}</MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ── Wellness disclaimer ──────────────────────────────────────── */}
        <MDBox px={1} mt={1}>
          <MDTypography variant="caption" color="secondary">
            COSMA Brain Wellness tracks your personal improvement over time. Scores reflect your individual progress
            and are not clinical measurements. For clinical information about your recovery, speak to your therapist.
          </MDTypography>
        </MDBox>

      </MDBox>
    </DashboardLayout>
  );
}
